import React, { useState, useEffect, useCallback } from "react";
import { jobCodeAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { useAuth } from "../AuthContext";
import { isAdminRole } from "../../utils/workforceUtils";
import StaffLayout from "./StaffLayout";
import "./JobCodeList.css";

/**
 * Workforce Settings — Job Code list management (admin only).
 *
 * Implements the design system §8 "Settings tab" pattern:
 * - free-text add (no hierarchy)
 * - inline rename
 * - soft-delete with confirmation (never hard-delete; existing TimeLog rows
 *   reference these codes as String per data-models §17.4.1)
 *
 * Per Roderick's jobCode-coupling-gap arbitration option (a) (2026-04-28),
 * this lives as a sibling admin screen reachable from the staff sidebar.
 */
const JobCodeList = () => {
  const { user } = useAuth();
  const [jobCodes, setJobCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newCodeLabel, setNewCodeLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const adminAccess = isAdminRole(user?.role);

  const fetchJobCodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobCodeAPI.list({ active: true });
      // Normalize against likely response shapes — the contract is still
      // pending a Simone reconcile, so be lenient.
      const list =
        response.data?.jobCodes ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);
      setJobCodes(list);
      setError("");
    } catch (err) {
      handleApiError(
        err,
        "JobCodeList.fetch",
        setError,
        "Error loading job codes. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!adminAccess) {
      setLoading(false);
      return;
    }
    fetchJobCodes();
  }, [adminAccess, fetchJobCodes]);

  // Backend (Simone) uses two fields per job code: `code` (machine slug,
  // path param) and `displayName` (admin label). We derive `code` from
  // the admin's free-text input via slugify.
  const slugifyToCode = (input) =>
    input
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 64) || "code";

  const handleAdd = async (e) => {
    e.preventDefault();
    const displayName = newCodeLabel.trim();
    if (!displayName) {
      return;
    }
    try {
      setAdding(true);
      await jobCodeAPI.create({
        code: slugifyToCode(displayName),
        displayName,
      });
      setNewCodeLabel("");
      await fetchJobCodes();
    } catch (err) {
      handleApiError(
        err,
        "JobCodeList.create",
        setError,
        "Error adding job code."
      );
    } finally {
      setAdding(false);
    }
  };

  const beginEdit = (jobCode) => {
    setEditingId(jobCode.code || jobCode._id || jobCode.id);
    setEditingValue(
      jobCode.displayName || jobCode.label || jobCode.name || ""
    );
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async (code) => {
    const displayName = editingValue.trim();
    if (!displayName) {
      return;
    }
    try {
      setActioningId(code);
      await jobCodeAPI.update(code, { displayName });
      setEditingId(null);
      setEditingValue("");
      await fetchJobCodes();
    } catch (err) {
      handleApiError(
        err,
        "JobCodeList.update",
        setError,
        "Error renaming job code."
      );
    } finally {
      setActioningId(null);
    }
  };

  const requestDelete = (code) => setPendingDeleteId(code);
  const cancelDelete = () => setPendingDeleteId(null);

  const confirmDelete = async () => {
    if (!pendingDeleteId) {
      return;
    }
    try {
      setActioningId(pendingDeleteId);
      await jobCodeAPI.remove(pendingDeleteId);
      setPendingDeleteId(null);
      await fetchJobCodes();
    } catch (err) {
      handleApiError(
        err,
        "JobCodeList.remove",
        setError,
        "Error removing job code."
      );
    } finally {
      setActioningId(null);
    }
  };

  if (!adminAccess) {
    return (
      <StaffLayout>
        <div className="jobcode-list-container">
          <h1>Workforce — Job Codes</h1>
          <p className="jobcode-rbac-warning" role="alert">
            You do not have permission to manage job codes. Contact an
            administrator if you need access.
          </p>
        </div>
      </StaffLayout>
    );
  }

  return (
    <StaffLayout>
      <div className="jobcode-list-container">
        <header className="jobcode-list-header">
          <h1>Workforce — Job Codes</h1>
          <p className="jobcode-list-subtitle">
            Manage the controlled vocabulary used by staff profiles
            (<code>primaryRole</code>) and time logs (<code>jobCode</code>).
            Soft-delete only — historical records keep their original code.
          </p>
        </header>

        {error && (
          <div className="jobcode-error" role="alert">
            {error}
          </div>
        )}

        <form className="jobcode-add-form" onSubmit={handleAdd}>
          <label htmlFor="jobcode-new" className="jobcode-add-label">
            Add a job code
          </label>
          <p className="jobcode-helper">
            Type a human-friendly label (e.g. "Front Desk"). The system will
            derive a machine code (<code>front_desk</code>) automatically.
          </p>
          <div className="jobcode-add-row">
            <input
              id="jobcode-new"
              type="text"
              className="jobcode-input"
              value={newCodeLabel}
              onChange={(e) => setNewCodeLabel(e.target.value)}
              placeholder="e.g. Front Desk, Cleaning, Manager"
              maxLength={64}
              aria-required="true"
            />
            <button
              type="submit"
              className="jobcode-btn jobcode-btn-primary"
              disabled={adding || !newCodeLabel.trim()}
            >
              {adding ? "Adding..." : "Add"}
            </button>
          </div>
        </form>

        <section className="jobcode-list-section">
          <h2>Active job codes ({jobCodes.length})</h2>
          {loading ? (
            <p className="jobcode-loading">Loading...</p>
          ) : jobCodes.length === 0 ? (
            <p className="jobcode-empty">
              No active job codes yet. Add one above to get started.
            </p>
          ) : (
            <ul className="jobcode-list">
              {jobCodes.map((jobCode) => {
                // `code` is the canonical key (slug used as URL path
                // param). Fall back to legacy `_id` / `id` only for
                // graceful degradation if backend response shape drifts.
                const id = jobCode.code || jobCode._id || jobCode.id;
                const label =
                  jobCode.displayName ||
                  jobCode.label ||
                  jobCode.name ||
                  "";
                const isEditing = editingId === id;
                const isActioning = actioningId === id;
                return (
                  <li key={id} className="jobcode-row">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          className="jobcode-input"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          maxLength={64}
                          autoFocus
                          aria-label={`Rename job code ${label}`}
                        />
                        <div className="jobcode-row-actions">
                          <button
                            type="button"
                            className="jobcode-btn jobcode-btn-primary"
                            onClick={() => saveEdit(id)}
                            disabled={isActioning || !editingValue.trim()}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="jobcode-btn jobcode-btn-secondary"
                            onClick={cancelEdit}
                            disabled={isActioning}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="jobcode-row-label">{label}</span>
                        <div className="jobcode-row-actions">
                          <button
                            type="button"
                            className="jobcode-btn jobcode-btn-secondary"
                            onClick={() => beginEdit(jobCode)}
                            disabled={isActioning}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            className="jobcode-btn jobcode-btn-danger"
                            onClick={() => requestDelete(id)}
                            disabled={isActioning}
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {pendingDeleteId && (
          <div
            className="jobcode-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="jobcode-delete-heading"
          >
            <div className="jobcode-modal-card">
              <h2 id="jobcode-delete-heading">Remove this job code?</h2>
              <p>
                The code will be soft-deleted. Existing time-log records keep
                their original code; staff profiles using it will need to be
                reassigned manually.
              </p>
              <div className="jobcode-modal-actions">
                <button
                  type="button"
                  className="jobcode-btn jobcode-btn-secondary"
                  onClick={cancelDelete}
                  disabled={actioningId === pendingDeleteId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="jobcode-btn jobcode-btn-danger"
                  onClick={confirmDelete}
                  disabled={actioningId === pendingDeleteId}
                >
                  {actioningId === pendingDeleteId ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default JobCodeList;
